import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { authApi, onboardingApi, setToken, type OnboardingStep } from '@/api/client';
import { useAsync } from '@/hooks';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { BrandMark, SlashField } from '@/components/BrandMark';

export function SetupWizard() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminCreated, setAdminCreated] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 10) {
      setError('Password must be at least 10 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.setupAdmin({ name, email, password });
      setToken(res.token);
      // Admin account created — show the essentials-remaining checklist next
      // rather than jumping straight to the dashboard, so first-run setup
      // reads as a guided multi-step wizard through to a usable system.
      setAdminCreated(true);
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  if (adminCreated) {
    // Hydrate the auth session from the new token before leaving the wizard;
    // otherwise the route guard sees no user and bounces back to /login.
    const goTo = async (path: string) => {
      await refresh();
      navigate(path, { replace: true });
    };
    return <EssentialsRemainingStep onDone={() => void goTo('/app/admin')} onOpen={(href) => void goTo(href)} />;
  }

  return (
    <div className="setup-page">
      <div className="setup-hero">
        <BrandMark variant="light" size="lg" product="DispatchDash" />
        <h1 className="setup-headline">First-run setup</h1>
        <p className="setup-subtitle">Create the admin account, then finish the onboarding checklist.</p>
      </div>

      <form className="setup-form" onSubmit={handleSubmit}>
        <p className="kicker" style={{ color: 'var(--color-ink)' }}>Step 1 of 2</p>
        <h2>Create admin account</h2>

        {error && <div className="setup-error">{error}</div>}

        <Input
          label="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="Operations Manager"
        />

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="admin@11eleven.ph"
        />

        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={10}
          hint="At least 10 characters"
          autoComplete="new-password"
        />

        <Input
          label="Confirm password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />

        <Button type="submit" variant="signal" size="lg" fullWidth loading={loading}>
          Create admin & continue
        </Button>
      </form>

      <div className="setup-footer">
        <SlashField />
        <span>11Eleven Logistics Inc.</span>
      </div>

      <style>{`
        .setup-page {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--space-xl);
          background: var(--color-ink);
        }
        .setup-hero {
          width: 100%;
          max-width: 440px;
          margin-bottom: var(--space-xl);
        }
        .setup-headline {
          font-family: var(--font-display);
          font-size: 2rem;
          color: var(--color-white);
          margin: var(--space-lg) 0 var(--space-sm);
          letter-spacing: -0.02em;
        }
        .setup-subtitle {
          color: var(--color-steel);
          font-size: 0.9375rem;
        }
        .setup-form {
          background: var(--color-white);
          padding: var(--space-xl);
          border-radius: var(--radius-md);
          width: 100%;
          max-width: 440px;
          box-shadow: var(--shadow-md);
        }
        .setup-form h2 {
          margin-bottom: var(--space-lg);
          color: var(--color-ink);
        }
        .setup-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
        .setup-footer {
          width: 100%;
          max-width: 440px;
          margin-top: var(--space-2xl);
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          color: var(--color-steel);
          font-size: 0.75rem;
        }
      `}</style>
    </div>
  );
}

/** Step 2 of first-run setup: the admin account exists, but the system needs
 * a handful of essentials (zones, rate card, a vehicle, a client, …) before
 * trips can be created. Fetches live onboarding status rather than a static
 * list, and links straight to each remaining step. */
function EssentialsRemainingStep({
  onDone,
  onOpen,
}: {
  onDone: () => void;
  onOpen: (href: string) => void;
}) {
  const { data: steps, loading, error, reload } = useAsync(() => onboardingApi.checklist(), []);

  const essentialRemaining = (steps ?? []).filter((s) => s.essential && !s.completed);
  const optionalRemaining = (steps ?? []).filter((s) => !s.essential && !s.completed);
  const allEssentialDone = !loading && !error && essentialRemaining.length === 0;

  return (
    <div className="setup-page">
      <div className="setup-hero">
        <BrandMark variant="light" size="lg" product="DispatchDash" />
        <h1 className="setup-headline">Almost there</h1>
        <p className="setup-subtitle">
          Admin account created. Finish these essentials before dispatch can book real trips.
        </p>
      </div>

      <div className="setup-form setup-checklist-card">
        <p className="kicker" style={{ color: 'var(--color-ink)' }}>Step 2 of 2</p>
        <h2>Setup checklist</h2>

        {loading ? (
          <div className="loading-center" style={{ padding: 'var(--space-lg) 0' }}><div className="spinner" /></div>
        ) : error ? (
          <div className="setup-error">
            Couldn't load the setup checklist. {error}{' '}
            <Button size="sm" variant="secondary" onClick={() => void reload()}>Retry</Button>
          </div>
        ) : allEssentialDone ? (
          <p className="setup-all-done">All essential setup steps are complete — you're ready to dispatch.</p>
        ) : (
          <>
            <p className="setup-section-label">Essentials remaining ({essentialRemaining.length})</p>
            <ol className="setup-steps">
              {essentialRemaining.map((step) => (
                <StepRow key={step.id} step={step} onOpen={onOpen} />
              ))}
            </ol>
          </>
        )}

        {optionalRemaining.length > 0 && (
          <>
            <p className="setup-section-label">Recommended (optional)</p>
            <ol className="setup-steps">
              {optionalRemaining.map((step) => (
                <StepRow key={step.id} step={step} onOpen={onOpen} />
              ))}
            </ol>
          </>
        )}

        <Button variant="signal" size="lg" fullWidth onClick={onDone} style={{ marginTop: 'var(--space-lg)' }}>
          {allEssentialDone ? 'Go to dashboard' : 'Continue to dashboard'}
        </Button>
      </div>

      <div className="setup-footer">
        <SlashField />
        <span>11Eleven Logistics Inc.</span>
      </div>

      <style>{`
        .setup-checklist-card { max-width: 520px; }
        .setup-all-done {
          color: var(--color-forest);
          font-weight: 600;
          margin: var(--space-md) 0 var(--space-lg);
        }
        .setup-section-label {
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--color-steel);
          margin: var(--space-lg) 0 var(--space-sm);
        }
        .setup-steps { list-style: none; padding: 0; margin: 0; }
        .setup-step-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-md);
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
        }
        .setup-step-row:last-child { border-bottom: none; }
        .setup-step-label { color: var(--color-ink); font-size: 0.9375rem; }
      `}</style>
    </div>
  );
}

function StepRow({ step, onOpen }: { step: OnboardingStep; onOpen: (href: string) => void }) {
  return (
    <li className="setup-step-row">
      <span className="setup-step-label">{step.label}</span>
      {step.href && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => step.href && onOpen(step.href)}
        >
          Open
        </Button>
      )}
    </li>
  );
}
